export type SpiceLevel = 1 | 2 | 3 | 4 | 5;

// Deleting a leg drops the level to null: it no longer describes the multi.
export type MysteryMulti = { level: SpiceLevel | null };

export type Bracket = { min: number; max: number };

type SpiceLevelEntry = {
  level: SpiceLevel;
  label: string;
  description: string;
};

const levelEntries = {
  1: { level: 1, label: "Steady", description: "Easy does it." },
  2: { level: 2, label: "Juicy", description: "Worth a punt." },
  3: { level: 3, label: "Spicy", description: "Getting interesting." },
  4: { level: 4, label: "Big Swing", description: "Long shot territory." },
  5: { level: 5, label: "Hail Mary", description: "Say a little prayer." },
} satisfies Record<SpiceLevel, SpiceLevelEntry>;

export const spiceLevels = Object.values(levelEntries);

export const defaultLevel: SpiceLevel = 3;

export const entryForLevel = (level: SpiceLevel): SpiceLevelEntry =>
  levelEntries[level];

export const levelAfter = (level: SpiceLevel): SpiceLevel => {
  const index = spiceLevels.findIndex((entry) => entry.level === level);
  return spiceLevels[(index + 1) % spiceLevels.length].level;
};

export const isSpiceLevel = (
  value: number | null | undefined,
): value is SpiceLevel => spiceLevels.some((entry) => entry.level === value);

export type LevelSpec = {
  pricedOddsTarget: number;
  bracket: Bracket;
  legCounts: number[];
};

// both ends included: 2 to 5 gives [2, 3, 4, 5]
const everyWholeNumberFrom = (first: number, last: number): number[] =>
  Array.from({ length: last - first + 1 }, (_, offset) => first + offset);

// The target is the bracket's geometric centre, so equal headroom means equal ratio to each edge.
export const levelSpecFrom = ({
  minOdds,
  maxOdds,
  minLegs,
  maxLegs,
}: {
  minOdds: number;
  maxOdds: number;
  minLegs: number;
  maxLegs: number;
}): LevelSpec => ({
  pricedOddsTarget: Math.sqrt(minOdds * maxOdds),
  bracket: { min: minOdds, max: maxOdds },
  legCounts: everyWholeNumberFrom(minLegs, maxLegs),
});

// a multi needs two legs; a redundancy strip can take a SEM below it
export const minSemLegs = 2;

// floor the draw assumes when checking reach
export const minReachableLegOdds = 1.15;
// how far a leg may stray from ideal
export const legSpreadFactor = 2.2;

// re-walks of one shape before giving up
export const drawAttemptsPerShape = 6;

// SEMs carried by one pricing call
export const trialSemsPerBatch = 3;

// pricing rounds before the spin gives up
export const maxPricingAttempts = 5;

// how far past the bracket a price may sit
export const fallbackBracketWiden = 1.4;
// how long prices settle before re-pricing
export const repriceDebounceMs = 2000;
